import { Suspense, useMemo } from "react";
import { Provider } from "react-redux";
import { createBrowserRouter, RouterProvider } from "react-router-dom";

import { StyledEngineProvider, ThemeProvider } from "@mui/material";

import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

import * as Sentry from "@sentry/react";
import { SnackbarProvider } from "notistack";

import { customTheme, I18nProvider, LayoutSplashScreen } from "_metronic";

import ErrorPage from "app/pages/errors";
import { createRoutes } from "app/router/Routes";

// Initialize Stripe only if API key is available
const stripeApiKey = import.meta.env.VITE_STRIPE_API_KEY;
const stripePromise = stripeApiKey ? loadStripe(stripeApiKey) : null;

// Sentry Error Boundary Fallback Component
function SentryErrorFallback() {
  return <ErrorPage />;
}

export default function App({ store, basename }) {
  // Create router with route configuration
  const router = useMemo(() => {
    return createBrowserRouter(createRoutes(), {
      basename: basename,
    });
  }, [basename]);

  return (
    <Sentry.ErrorBoundary fallback={SentryErrorFallback} showDialog>
      <Provider store={store}>
        {/* Asynchronously persist redux stores and show `SplashScreen` while it's loading. */}
        {/* Add high level `Suspense` in case if was not handled inside the React tree. */}
        <Suspense fallback={<LayoutSplashScreen />}>
          {/* Provide Metronic theme overrides. */}
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={customTheme}>
              {/* Provide `react-intl` context synchronized with Redux state.  */}
              <I18nProvider>
                <SnackbarProvider
                  maxSnack={5}
                  classes={{
                    variantSuccess: "bg-success text-light",
                    variantError: "bg-error text-light",
                    variantWarning: "bg-warning text-light",
                    // variantPrimary: 'bg-primary text-light',
                    variantInfo: "bg-info text-light",
                  }}
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  autoHideDuration={3000}
                >
                  {stripePromise ? (
                    <Elements stripe={stripePromise}>
                      <RouterProvider router={router} fallbackElement={<LayoutSplashScreen />} />
                    </Elements>
                  ) : (
                    <RouterProvider router={router} fallbackElement={<LayoutSplashScreen />} />
                  )}
                </SnackbarProvider>
              </I18nProvider>
            </ThemeProvider>
          </StyledEngineProvider>
        </Suspense>
      </Provider>
    </Sentry.ErrorBoundary>
  );
}
